import React, { useState, useEffect } from 'react';
import { Menu, Pressable, ChevronDownIcon, FormControl, Input, Actionsheet, VStack, SmallCloseIcon } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';
import { Caption } from '~/shared/Typography';
import OurPressable from '~/shared/Pressable';
import { i18nFn, useAppSettings, useSpringBigStore } from '@springbig/shared';
import { RestrictionsDisclaimer } from '~/pages/giftCard/RestrictionsDisclaimer';

const AmountDropdown = ({
    amount,
    setAmount,
    t,
    amounts
}: {
    amount: number,
    setAmount: React.Dispatch<React.SetStateAction<number>>,
    t: i18nFn,
    amounts: Array<number>
}) => {

    const { member, merchant } = useSpringBigStore();

    const maxPointPerPeriodCorrectedByMaxDollarAmount = Math.floor(
        (Number(member?.remaining_dollar_amount_for_current_period) / 100)
        * Number(merchant.dollars_per_point)
    );

    const maxAmount = Math.min(
        Number(member?.remaining_dollar_amount_for_current_period) / 100,
        Number(merchant?.gc_dollar_amount_one_time)
    );

    const MENU_ITEMS = [...amounts, 'other'];

    const gStyle = useGeneralStyles();
    const [inputValue, setInputValue] = useState('');
    const [isOtherSelected, setIsOtherSelected] = useState(false);
    const [isOpen, setMenuOpen] = useState(false);

    const handlePress = () => {

        setMenuOpen(!isOpen);
    };

    const onMenuItemSelected = (index: number) => {

        if (index < MENU_ITEMS.length - 1) {
            setAmount(MENU_ITEMS[index] as number);
            setInputValue(MENU_ITEMS[index].toString());
            setIsOtherSelected(false);
        }
        else {
            setIsOtherSelected(true);
        }
    };

    const onAmountChange = (amountInput: string) => {

        let updatedAmount = amountInput.replace(/[^0-9]/g, '');
        if (amountInput.length > 3) {
            updatedAmount = updatedAmount.slice(0, 3);
        }

        setInputValue(updatedAmount);
        setAmount(parseFloat(updatedAmount));
    };

    const debounceAmountNormalization = (): void => {

        const updatedAmount = Math.min(maxAmount, amount).toString();

        setInputValue(updatedAmount);
        setAmount(parseFloat(updatedAmount));
    };

    return (
        <FormControl>
            <Input
                variant="underlined"
                p={2} keyboardType="numeric"
                placeholder="amount"
                value={`$${isOtherSelected ? inputValue : amount}`}
                onChangeText={onAmountChange}
                onBlur={debounceAmountNormalization}
                isDisabled={!isOtherSelected}
                fontSize={18}
                fontWeight={300}
                backgroundColor={'transparent'}
                color={gStyle.text}
                onPressIn={() => setIsOtherSelected(true)}
                borderBottomWidth={0.5}
                borderBottomColor={gStyle.text}
                returnKeyType='done'
                InputRightElement={
                    <Menu
                        padding={4}
                        borderRadius={24}
                        borderColor={gStyle.button_background}
                        backgroundColor={gStyle.screen_background}
                        borderWidth={1}
                        placement="bottom"
                        mr={4}
                        mb={2}
                        defaultIsOpen={false}
                        isOpen={isOpen}
                        onOpen={() => handlePress()}
                        onClose={() => handlePress()}
                        trigger={triggerProps => {

                            return <Pressable accessibilityLabel="More options menu" {...triggerProps}>
                                <ChevronDownIcon color={gStyle.button_background} size='lg' />
                            </Pressable>;
                        }}>
                        {MENU_ITEMS.map((item, index) => {

                            const isSelected = (item === 'other' && isOtherSelected) ? true : (isOtherSelected ? false : amount === item);

                            return (
                                <Menu.Item
                                    key={item}
                                    backgroundColor={isSelected ? gStyle.button_background : 'transparent'}
                                    opacity={isSelected ? 0.5 : 1}
                                    borderRadius={12}
                                    padding={0}
                                >
                                    <OurPressable marginX={-2} paddingY={2} paddingX={4} onPress={() => onMenuItemSelected(index)}>
                                        <Caption
                                            fontSize={18}
                                            textAlign={'center'}
                                            fontWeight={isSelected ? 900 : 200}
                                        >
                                            {item === 'other' ? 'other' : `$ ${item}.00`}
                                        </Caption>
                                    </OurPressable>
                                </Menu.Item>);
                        }
                        )}
                    </Menu>
                }
            />
            { member && merchant && merchant.gift_card_restrictions &&
                <RestrictionsDisclaimer t={t} type={'dollar'} maxPeriod={maxPointPerPeriodCorrectedByMaxDollarAmount} maxOneTime={maxAmount} />
            }
        </FormControl>
    );
};

export default AmountDropdown;
